import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { endpoints } from "@/lib/api";
import { useFan } from "@/lib/fanContext";
import { toast } from "sonner";
import { Heart, MapPin, X, CalendarDays, ArrowUpRight } from "lucide-react";

export default function FanFollowing() {
    const { refresh } = useFan();
    const [artists, setArtists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState({ genre: "", city: "", country: "" });

    const load = () => {
        setLoading(true);
        const params = {};
        Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
        endpoints.fanFollowing(params).then((d) => {
            setArtists(d.artists);
            setLoading(false);
        }).catch(() => setLoading(false));
    };

    useEffect(load, [filters]);

    const genres = useMemo(() => [...new Set(artists.map((a) => a.genre))].sort(), [artists]);
    const cities = useMemo(() => [...new Set(artists.map((a) => a.city))].sort(), [artists]);
    const countries = useMemo(() => [...new Set(artists.map((a) => a.country))].sort(), [artists]);

    // Get unfiltered list once for filter options (or just use current data)
    const [allForOptions, setAllForOptions] = useState([]);
    useEffect(() => {
        endpoints.fanFollowing().then((d) => setAllForOptions(d.artists)).catch(() => {});
    }, []);

    const opt = (list) => [...new Set(list)].sort();
    const genreOpts = opt(allForOptions.map((a) => a.genre));
    const cityOpts = opt(allForOptions.map((a) => a.city));
    const countryOpts = opt(allForOptions.map((a) => a.country));

    const unfollow = async (a) => {
        try {
            await endpoints.fanUnfollow(a.id);
            toast(`Unfollowed ${a.name}`);
            await refresh();
            load();
            endpoints.fanFollowing().then((d) => setAllForOptions(d.artists));
        } catch {
            toast.error("Unfollow failed");
        }
    };

    const clearFilters = () => setFilters({ genre: "", city: "", country: "" });

    return (
        <div data-testid="fan-following-page" className="p-4 md:p-6">
            {/* Filters */}
            <div className="flex flex-col md:flex-row md:items-center gap-3 border-b border-[#2a2a2a] pb-4 mb-4">
                <Select label="Genre" value={filters.genre} options={genreOpts} onChange={(v) => setFilters({ ...filters, genre: v })} testId="following-filter-genre" />
                <Select label="City" value={filters.city} options={cityOpts} onChange={(v) => setFilters({ ...filters, city: v })} testId="following-filter-city" />
                <Select label="Country" value={filters.country} options={countryOpts} onChange={(v) => setFilters({ ...filters, country: v })} testId="following-filter-country" />
                <button onClick={clearFilters} className="pm-btn" data-testid="following-clear-filters">
                    <X className="w-3 h-3 mr-1" /> Clear
                </button>
                <div className="md:ml-auto text-xs font-mono uppercase text-[#666]" data-testid="following-count">
                    {loading ? "…" : `${artists.length} artist${artists.length === 1 ? "" : "s"}`}
                </div>
            </div>

            {loading ? (
                <div className="font-mono text-sm text-[#666]">Loading…</div>
            ) : artists.length === 0 ? (
                <div className="border border-dashed border-[#2a2a2a] p-10 text-center" data-testid="following-empty">
                    <Heart className="w-8 h-8 text-[#666] mx-auto" />
                    <div className="mt-3 text-sm text-[#a3a3a3]">
                        You aren't following anyone yet. Discover artists and hit Follow.
                    </div>
                    <Link to="/artists" className="pm-btn pm-btn-primary mt-4 inline-flex" data-testid="following-browse-cta">
                        Browse artists
                    </Link>
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#2a2a2a] border border-[#2a2a2a]">
                    {artists.map((a) => (
                        <div key={a.id} className="bg-[#0a0a0a] p-4 flex flex-col group" data-testid={`following-card-${a.id}`}>
                            <div className="relative aspect-[4/3] overflow-hidden">
                                <img src={a.image_url} alt="" className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500" />
                                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                                <div className="absolute bottom-3 left-3 right-3">
                                    <div className="text-[10px] font-mono uppercase tracking-widest text-[#ff4400]">{a.genre}</div>
                                    <div className="font-display font-black uppercase text-xl leading-tight text-[#f4f4f0] mt-1">{a.name}</div>
                                </div>
                            </div>
                            <div className="mt-3 flex items-center gap-2 text-xs text-[#a3a3a3]">
                                <MapPin className="w-3 h-3" /> {a.city}, {a.country}
                            </div>
                            <div className="mt-2 text-[10px] font-mono uppercase text-[#a3a3a3] flex items-center gap-1">
                                <CalendarDays className="w-3 h-3 text-[#ff4400]" /> {a.upcoming_event_count || 0} upcoming event{a.upcoming_event_count === 1 ? "" : "s"}
                            </div>
                            <div className="mt-3 flex gap-2">
                                <Link to={`/artists/${a.id}`} className="pm-btn text-xs flex-1" data-testid={`following-view-${a.id}`}>
                                    View <ArrowUpRight className="w-3 h-3 ml-1" />
                                </Link>
                                <button onClick={() => unfollow(a)} className="pm-btn text-xs" data-testid={`following-unfollow-${a.id}`}>
                                    Unfollow
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function Select({ label, value, options, onChange, testId }) {
    return (
        <select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            data-testid={testId}
            className="bg-transparent border border-[#2a2a2a] px-3 py-2 text-sm font-mono uppercase text-[#f4f4f0] hover:border-[#f4f4f0] focus:border-[#ff4400] outline-none"
        >
            <option value="" className="bg-[#141414]">{label}: All</option>
            {options.map((o) => (
                <option key={o} value={o} className="bg-[#141414]">{o}</option>
            ))}
        </select>
    );
}
